import React, { useState, useEffect } from 'react';
import { Container, Grid2 as Grid, Paper, Typography, Button, Box, Alert, Chip, Divider, Fade } from '@mui/material';
import { styled } from '@mui/system';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from "../AuthContext";
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import CryptoJS from 'crypto-js';

// Modern color palette (consistent with ContactUs)
const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  accent: '#3498db',
  background: '#f5f7fa',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};


const CYBERSOURCE_CONFIG = {
  ACCESS_KEY: '0105d945f85c368cbec7459cc6d30465',
  PROFILE_ID: '14098329-8313-4F96-909A-3C957A276641',
  SECRET_KEY: '2052d69cb9794c9991ed44a1efa528a394f84c9653ec4077af586faa177591fd71c7a66e055a4371bc163c6e80ebd36f8339a7f784f7478eac4dc810b0531459f751b7399aa84a649c9430b0f5e89c2f86dd0fa12299406fbadd2e9e7b8823d7f0ada056879a459eaf110d019f69e36a1754fc747bc042b7b4122f08f390624b',
  URL: 'https://secureacceptance.cybersource.com/pay',
};

// Styled components
const PricingPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  borderRadius: '20px',
  background: '#ffffff',
  boxShadow: '0 10px 40px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  minHeight: '400px',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 15px 50px rgba(0, 0, 0, 0.1)',
  },
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
    minHeight: '380px',
  },
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  background: `linear-gradient(90deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
  padding: theme.spacing(2),
  borderRadius: '15px 15px 0 0',
  color: '#fff',
  textAlign: 'center',
  margin: '-32px -32px 32px -32px',
  [theme.breakpoints.down('md')]: {
    margin: '-24px -24px 24px -24px',
    padding: theme.spacing(1.5),
  },
}));

const PricingButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
  background: `linear-gradient(90deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
  color: 'white',
  borderRadius: '12px',
  padding: theme.spacing(1.5, 4),
  fontWeight: 600,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    background: `linear-gradient(90deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
    transform: 'scale(1.05)',
    boxShadow: '0 4px 15px rgba(46, 204, 113, 0.3)',
  },
  '&:disabled': {
    background: 'grey',
    color: 'white',
    cursor: 'not-allowed',
  },
}));

const PriceText = styled(Typography)(({ theme }) => ({
  fontSize: '2.2rem',
  fontWeight: 700,
  color: colors.primary,
  [theme.breakpoints.down('md')]: {
    fontSize: '1.8rem',
  },
}));

const DurationText = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  color: colors.secondary,
  opacity: 0.9,
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    fontSize: '1rem',
  },
}));

const FeatureItem = styled(Typography)(({ theme }) => ({
  color: 'black',
  marginBottom: theme.spacing(1.5),
  fontSize: '0.9rem',
  [theme.breakpoints.down('md')]: {
    fontSize: '0.9rem',
  },
}));

const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userStatus, setUserStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
   const [subscription, setSubscription] = useState(null);

  // Fetch user status from Firestore
  useEffect(() => {
    const fetchUserData = async () => {
      if (user) {
        try {
          const userDocRef = doc(db, `users/${user.uid}`);
          const userDoc = await getDoc(userDocRef);
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserStatus(data.status || 'trial');
            setSubscription(data.subscription || null);
          } else {
            console.log('Pricing: User document not found');
            setUserStatus('trial');
            setSubscription(null);
          }
        } catch (err) {
          console.error('Pricing: Error fetching user data:', { message: err.message, code: err.code });
          setError('Failed to load user data. Please try again.');
        }
      }
    };
    fetchUserData();
  }, [user]);

    // Check if subscription is expired
  const isSubscriptionExpired = () => {
    if (!subscription || !subscription.expiresAt) return true;
    const expiresAt = subscription.expiresAt.toDate ? subscription.expiresAt.toDate() : new Date(subscription.expiresAt);
    return expiresAt < new Date();
  };

  // Pricing plans data
  const plans = [
    {
      title: 'Free Trial',
      price: 'Free',
      duration: '7 Days',
      features: [
        '✅ Limited access to selected lessons, quizzes, solutions, and worksheets',
      ],
      period: 'trial',
      highlighted: false,
    },
    {
      title: 'Monthly Plan',
      price: 'Rs300',
      duration: 'per month',
      features: [
        '✅ Full access to all lesson videos, quizzes, worksheets, and detailed solutions',
        '✅ Priority response to your questions and learning queries',
      ],
      amount: '300',
      period: 'monthly',
      highlighted: false,
    },
    {
      title: 'Termly Plan',
      price: 'Rs750',
      duration: 'per 3 months',
      subText: 'Save Rs150!',
      features: [
        '✅ Full access to all lesson videos, quizzes, worksheets, and detailed solutions',
        '✅ Priority response to your questions and learning queries',
        '✅ Perfect for staying on track throughout the school term',
      ],
      amount: '750',
      period: 'termly',
      highlighted: true,
    },
    {
      title: 'Yearly Plan',
      price: 'Rs3000',
      duration: 'per year',
      subText: 'Save Rs600!',
      features: [
        '✅ Enjoy everything in the Monthly Plan',
        '✅ One-time payment for the year – more value for less',
      ],
      amount: '3000',
      period: 'yearly',
      highlighted: false,
    },
  ];

  // CyberSource payment data generation
  const generatePaymentData = (plan) => {
    const transactionUuid = Math.random().toString(36).substring(2, 15);
    const signedDateTime = new Date().toISOString().replace(/\.[0-9]{3}/, '');
    const signedFieldNames =
      'access_key,profile_id,transaction_uuid,signed_date_time,transaction_type,reference_number,amount,currency,locale,signed_field_names';

    const data = {
      access_key: CYBERSOURCE_CONFIG.ACCESS_KEY,
      profile_id: CYBERSOURCE_CONFIG.PROFILE_ID,
      transaction_uuid: transactionUuid,
      signed_date_time: signedDateTime,
      transaction_type: 'sale',
      reference_number: `SUB_${plan.period}_${Date.now()}`,
      amount: plan.amount,
      currency: 'SCR',
      locale: 'en',
      signed_field_names: signedFieldNames,
      signature: '',
    };

    const dataString = Object.keys(data)
      .filter((key) => key !== 'signature')
      .map((key) => `${key}=${data[key]}`)
      .join(',');
    data.signature = CryptoJS.HmacSHA256(dataString, CYBERSOURCE_CONFIG.SECRET_KEY).toString(
      CryptoJS.enc.Base64
    );

    return data;
  };

  // Handle payment initiation
  const handleProceedToPayment = async (plan) => {
    if (!user || !plan.amount) {
      setError('Please log in to proceed with payment.');
      navigate('/sign-in');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = generatePaymentData(plan);
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = CYBERSOURCE_CONFIG.URL;

      Object.entries(formData).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      console.error('Pricing: Payment initiation error:', { message: err.message, code: err.code });
      setError('Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  // Feature item animation variants
  const featureVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.3,
        ease: 'easeOut',
      },
    }),
  };

  return (
    <Container maxWidth="lg" style={{ marginTop: 100, marginBottom: 80 }}>
      <Fade in timeout={800}>
        <Box>
          <Box sx={{ textAlign: 'center', mb: 5 }}>
            <Typography
              variant="h4"
              sx={{ fontWeight: 700, color: colors.secondary, mb: 1 }}
            >
              Choose Your Plan
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: colors.secondary, opacity: 0.8 }}
            >
              Unlock the best learning experience with Maths Gem Seychelles
            </Typography>
          </Box>

          {error && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
              <Alert
                severity="error"
                sx={{
                  width: { xs: '90%', md: '60%' },
                  borderRadius: '12px',
                  '& .MuiAlert-icon': {
                    color: colors.secondary,
                  },
                }}
              >
                {error}
              </Alert>
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 5 }}>
            <Alert
              sx={{
                width: { xs: '90%', md: '60%' },
                backgroundColor: '#fafafa',
                borderRadius: '12px',
                color: colors.secondary,
                '& .MuiAlert-icon': {
                  color: colors.primary,
                },
                '&:hover': {
                  backgroundColor: '#f0f4f8',
                },
              }}
              severity="info"
            >
              All prices are in Seychellois Rupees (Rs). Contact us for more details.
            </Alert>
          </Box>

          <Divider sx={{ my: 4, borderColor: 'rgba(0, 0, 0, 0.1)' }} />

          <Grid container spacing={4} justifyContent="center">
            {plans.map((plan, index) => {
              const isTrialOrPending = user && ['trial', 'pending payment'].includes(userStatus) && plan.period !== 'trial';
              const isPremium = user && userStatus === 'premium' && !isSubscriptionExpired();
              const isExpired = user && isSubscriptionExpired();
              const buttonText = !user
                ? 'Get Started'
                : isPremium
                ? 'Go to Dashboard'
                : isExpired && plan.period !== 'trial'
                ? 'Get Subscription'
                : 'Go to Dashboard';

              return (
                <Grid
                  key={index}
                  size={{ xs: 12, sm: 6, md: 3 }}
                >
                  <PricingPaper elevation={8}>
                    <HeaderBox>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 600 }}
                      >
                        {plan.title}
                      </Typography>
                    </HeaderBox>
                    <Box sx={{ flexGrow: 1 }}>
                      <PriceText>{plan.price}</PriceText>
                      {plan.subText && (
                        <Typography
                          variant="body2"
                          sx={{ color: colors.primary, fontWeight: 500, mb: 1 }}
                        >
                          {plan.subText}
                        </Typography>
                      )}
                      <DurationText>{plan.duration}</DurationText>
                      <Box component="ul" sx={{ listStyle: 'none', p: 0, mb: 3 }}>
                        {plan.features.map((feature, i) => (
                          <motion.div
                            key={i}
                            custom={i}
                            variants={featureVariants}
                            initial="hidden"
                            animate="visible"
                          >
                            <FeatureItem component="li">
                              {feature}
                            </FeatureItem>
                          </motion.div>
                        ))}
                      </Box>
                    </Box>
                    <Box>
                      {(isTrialOrPending || isExpired) && plan.period !== 'trial' ? (
                        <PricingButton
                          onClick={() => handleProceedToPayment(plan)}
                          disabled={loading}
                        >
                          {loading ? 'Processing...' : buttonText}
                        </PricingButton>
                      ) : (
                        <Link to={user ? '/dashboard' : '/sign-in'} style={{ textDecoration: 'none' }}>
                          <PricingButton disabled={loading || (user && isPremium && !isExpired)}>
                            {buttonText}
                          </PricingButton>
                        </Link>
                      )}
                    </Box>
                  </PricingPaper>
                </Grid>
              );
            })}
          </Grid>

          <Divider sx={{ my: 4, borderColor: 'rgba(0, 0, 0, 0.1)' }} />

          <Typography
            variant="body2"
            align="center"
            sx={{ color: colors.secondary, opacity: 0.8 }}
          >
            We’re here to assist you with your learning journey. Reach out anytime!
          </Typography>
        </Box>
      </Fade>
    </Container>
  );
};

export default Pricing;